<template>
  <div>
    <div class="mc-dashboard__widget-content mb-4">
      <div class="border border-1">
        <b-skeleton-wrapper :loading="loading">
          <template #loading>
            <b-row>
              <b-col md="12">
                <b-skeleton width="100%" height="450px" />
              </b-col>
            </b-row>
          </template>
          <table class="table b-table table-sm">
            <thead>
              <tr>
                <th width="18%" class="bg-teal-100"></th>
                <th v-for="data in tripsStatistics.data">
                  {{ `${localizeMonth(data.month)} ${data.year}` }}
                  <b-button v-if="data[data.length - 1]" variant="info" size="small" class="ms-3">
                    {{ $t('dashboard.text.selectedMonth') }}
                  </b-button>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>{{ $t('dashboard.label.vehicleUtilization') }}</th>
                <td v-for="data in tripsStatistics.data">
                  <div class="d-flex justify-content-between">
                    <span>
                      {{ data.utilizedVehicle.chosenValue }} {{ $t('global.object.of') }}
                      {{ data.totalVehicle }}
                    </span>
                    <McBadge
                      :text="
                        (data.utilizedVehicle.increased ? ' ' : '-') +
                        data.utilizedVehicle.diffPercent +
                        ' %'
                      "
                      :variant="data.utilizedVehicle.increased ? 'outlineSuccess' : 'outlineDanger'"
                      size="medium"
                      type="button"
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <th>{{ $t('dashboard.title.totalTrip') }}</th>
                <td v-for="data in tripsStatistics.data">
                  <div class="d-flex justify-content-between">
                    <span>{{ thousandFormat(data.totalTrip.chosenValue) }}</span>
                    <McBadge
                      :text="
                        (data.totalTrip.increased ? ' ' : '-') + data.totalTrip.diffPercent + ' %'
                      "
                      :variant="data.totalTrip.increased ? 'outlineSuccess' : 'outlineDanger'"
                      size="medium"
                      type="button"
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <th>{{ $t('dashboard.chart.totalDistance') }}</th>
                <td v-for="data in tripsStatistics.data">
                  <div class="d-flex justify-content-between">
                    <span>{{ `${thousandFormat(data.tripMileage.chosenValue)} Km` }}</span>
                    <McBadge
                      :text="
                        (data.tripMileage.increased ? ' ' : '-') +
                        data.tripMileage.diffPercent +
                        ' %'
                      "
                      :variant="data.tripMileage.increased ? 'outlineSuccess' : 'outlineDanger'"
                      size="medium"
                      type="button"
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <th>% {{ $t('dashboard.label.totalIdleOnEngine') }}</th>
                <td v-for="data in tripsStatistics.data">
                  <div class="d-flex justify-content-between">
                    <span>{{ data.idlePercentage.chosenValue }} %</span>
                    <McBadge
                      :text="
                        (data.idlePercentage.increased ? ' ' : '-') +
                        data.idlePercentage.diffPercent +
                        ' %'
                      "
                      :variant="!data.idlePercentage.increased ? 'outlineSuccess' : 'outlineDanger'"
                      size="medium"
                      type="button"
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <th>{{ $t('dashboard.label.totalIdle') }}</th>
                <td v-for="data in tripsStatistics.data">
                  <div class="d-flex justify-content-between">
                    <span>{{
                      data.idleDuration.chosenValue
                        ? secondsToHours(data.idleDuration.chosenValue)
                        : 0
                    }}</span>
                    <McBadge
                      :text="
                        (data.idleDuration.increased ? ' ' : '-') +
                        data.idleDuration.diffPercent +
                        ' %'
                      "
                      :variant="!data.idleDuration.increased ? 'outlineSuccess' : 'outlineDanger'"
                      size="medium"
                      type="button"
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <th>{{ $t('dashboard.title.totalFuel') }}</th>
                <td v-for="data in tripsStatistics.data">
                  <div class="d-flex justify-content-between">
                    <span>{{ data.fuelConsumption.chosenValue }} L</span>
                    <McBadge
                      :text="
                        (data.fuelConsumption.increased ? ' ' : '-') +
                        data.fuelConsumption.diffPercent +
                        ' %'
                      "
                      :variant="
                        !data.fuelConsumption.increased ? 'outlineSuccess' : 'outlineDanger'
                      "
                      size="medium"
                      type="button"
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <th>{{ $t('dashboard.label.totalFuelCost') }}</th>
                <td v-for="data in tripsStatistics.data">
                  <div class="d-flex justify-content-between">
                    <span>
                      {{
                        `Rp ${
                          data.fuelPrice.chosenValue
                            ? thousandFormat(data.fuelPrice.chosenValue)
                            : '0,-'
                        }`
                      }}
                    </span>
                    <McBadge
                      :text="
                        (data.fuelPrice.increased ? ' ' : '-') + data.fuelPrice.diffPercent + ' %'
                      "
                      :variant="!data.fuelPrice.increased ? 'outlineSuccess' : 'outlineDanger'"
                      size="medium"
                      type="button"
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <th>{{ $t('dashboard.label.fuelEfficiency') }}</th>
                <td v-for="data in tripsStatistics.data">
                  <div class="d-flex justify-content-between">
                    <span>{{ `${data.fuelEfficiency.chosenValue} Km/L` }} </span>
                    <McBadge
                      :text="
                        (data.fuelEfficiency.increased ? ' ' : '-') +
                        data.fuelEfficiency.diffPercent +
                        ' %'
                      "
                      :variant="data.fuelEfficiency.increased ? 'outlineSuccess' : 'outlineDanger'"
                      size="medium"
                      type="button"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </b-skeleton-wrapper>
      </div>
    </div>
  </div>
</template>

<script>
import { Helper } from '@/mixin/helper'
import McBadge from '@/components/McBadge'

/**
 * Dashboard Component
 */
export default {
  components: {
    McBadge,
  },

  mixins: [Helper],

  props: {
    loading: {
      type: Boolean,
    },
    tripsStatistics: {
      type: Object,
    },
  },
}
</script>
